import api from './api';
import { API_BASE_URL } from '../lib/constants';
import { extractBackendErrorMessage } from './videoGenerationService';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const resolveAudioUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return url.startsWith('/') ? `${API_BASE_URL}${url}` : `${API_BASE_URL}/${url}`;
};

export const resolveAudioDownloadUrl = (url) => {
  const resolved = resolveAudioUrl(url);
  if (!resolved) return '';
  return resolved.endsWith('/download') ? resolved : `${resolved}/download`;
};

const normalizeStatus = (status) => String(status || '').trim().toUpperCase();

const buildError = (fallback, payload) => {
  const message = extractBackendErrorMessage(payload) || fallback;
  const error = new Error(message);
  error.payload = payload;
  return error;
};

const toAudioRecord = (payload = {}) => {
  const audioUrl = pickAudioUrl(payload);
  return {
    ...payload,
    audioUrl: resolveAudioUrl(audioUrl),
    downloadUrl: resolveAudioDownloadUrl(payload.downloadUrl || audioUrl),
    coverUrl: resolveAudioUrl(payload.coverUrl),
  };
};

const pickAudioUrl = (payload = {}) => (
  payload.audioUrl || payload.fileUrl || payload.mediaUrl || payload.url || ''
);

export async function generateLyrics(payload) {
  const response = await api.post('/generate-audio/lyrics-assistant', payload);
  return response.data || {};
}

export async function updateLyrics(id, lyrics) {
  const response = await api.put(`/generate-audio/${id}/lyrics`, { lyrics });
  return response.data || {};
}

export async function generateTextAudio(payload) {
  const response = await api.post('/generate-audio/text', payload);
  return response.data || {};
}

export async function generateImageAudio(files, payload) {
  const formData = new FormData();
  files.slice(0, 5).forEach((file) => formData.append('images', file));
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });
  const response = await api.post('/generate-audio/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data || {};
}

export async function getAudioGeneration(id) {
  const response = await api.get(`/generate-audio/${id}`);
  return response.data || {};
}

export async function downloadAudioFile(url, filename) {
  const downloadUrl = resolveAudioDownloadUrl(url);
  if (!downloadUrl) throw new Error('No audio URL');
  const blob = await fetchAudioBlob(downloadUrl);
  saveBlob(blob, filename || filenameFromUrl(downloadUrl));
}

export async function fetchAudioBlob(url) {
  const audioUrl = resolveAudioUrl(url);
  if (!audioUrl) throw new Error('No audio URL');
  const response = await fetch(audioUrl, { headers: authHeaders() });
  if (!response.ok) throw new Error(`Audio request failed: ${response.status}`);
  return response.blob();
}

export async function pollAudioGeneration(id, options = {}) {
  const intervalMs = options.intervalMs || 2500;
  const timeoutMs = options.timeoutMs || 300000;
  const startedAt = Date.now();
  while (true) {
    const payload = await getAudioGeneration(id);
    const status = normalizeStatus(payload.status);
    if (status === 'COMPLETED') return toAudioRecord(payload);
    if (status === 'FAILED') throw buildError('Audio generation failed', payload);
    if (Date.now() - startedAt > timeoutMs) {
      throw buildError('Audio generation timed out', payload);
    }
    await sleep(intervalMs);
  }
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function saveBlob(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || 'music.mp3';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

function filenameFromUrl(url) {
  const clean = String(url).split('?')[0].replace(/\/download$/, '');
  const name = clean.split('/').pop();
  return name || 'music.mp3';
}
